import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { Logo, Wordmark } from "@/components/Logo";
import { resubscribe, unsubscribe } from "@/lib/api";

export default function Unsubscribe() {
  const { token } = useParams();
  const [state, setState] = useState("working"); // working | done | error

  useEffect(() => {
    unsubscribe(token)
      .then(() => setState("done"))
      .catch((e) => setState(e?.response?.status === 404 ? "error" : "done"));
  }, [token]);

  const resume = async () => {
    try {
      await resubscribe(token);
      toast.success("Welcome back in. The next letter is yours.");
      setState("done");
    } catch {
      toast.error("We couldn't do that just now. Please try again.");
    }
  };

  return (
    <div data-testid="unsubscribe-page" className="min-h-screen flex flex-col">
      <header className="px-6 pt-8">
        <Link to="/" className="inline-flex items-center gap-3" aria-label="AuDHDSpaces home">
          <Logo size={30} />
          <Wordmark />
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-20">
        {state === "working" && <p className="text-moss animate-pulse">One moment…</p>}

        {state === "done" && (
          <div className="text-center max-w-md">
            <div className="h-20 w-20 mx-auto rounded-full bg-sage-100 flex items-center justify-center mb-8">
              <Check className="h-8 w-8 text-sage-700" strokeWidth={2.5} />
            </div>
            <h1 className="font-serif text-4xl text-ink mb-5">You're out — gently.</h1>
            <p className="text-moss leading-relaxed mb-10">
              You won't hear from us again. If you ever want back in, the door is open —
              no hard feelings either way.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
              <button
                type="button"
                data-testid="resubscribe-button"
                onClick={resume}
                className="rounded-full bg-sage-700 px-8 py-3.5 text-sm font-semibold text-paper hover:bg-sage-900 transition-colors duration-300"
              >
                Resume the letters
              </button>
              <Link
                to={`/preferences/${token}`}
                data-testid="preferences-link"
                className="link-quiet text-sm"
              >
                Update your answers instead
              </Link>
            </div>
          </div>
        )}

        {state === "error" && (
          <div className="text-center max-w-md">
            <h1 className="font-serif text-3xl text-ink mb-4">This link went quiet.</h1>
            <p className="text-moss mb-8">
              We couldn't find that subscription link. The bottom of any letter from us
              carries a fresh one.
            </p>
            <Link to="/" className="link-quiet">Back to the home page</Link>
          </div>
        )}
      </main>
    </div>
  );
}
